import { createPortal } from 'react-dom'
import { useEffect, useRef, useState } from 'react'
import { useRoomStore, useRoomStoreActions } from '@/stores/room'
import { useRoomSessionStore } from '@/stores/roomSession'
import { UncontrolledTooltip } from '@/components/CustomTooltip'
import { Icon } from '@/components/common/icons/Icon'
import { cn, isElementTruncated } from '@/helpers/utils'
import type { RoomMemberEntity } from '@/helpers/types'
import { useUiStore, useUiStoreActions } from '@/stores/ui'
import { Button } from '@/components/base/button'
import { Feature } from '@/components/Feature'
import { FEATURES } from '@/stores/features'

export const MemberOverlays = () => {
  const membersMap = useRoomStore(state => state.membersMap)
  const { getMembersState } = useRoomStoreActions()

  const members = getMembersState(membersMap)

  return members.map(member => (
    <MemberOverlay
      audioMuted={member.audioMuted}
      handRaised={member.handRaised}
      key={member.memberId}
      memberId={member.memberId}
      name={member.name}
      talking={member.talking}
    />
  ))
}

type MemberOverlayProps = Pick<
  RoomMemberEntity,
  'audioMuted' | 'handRaised' | 'memberId' | 'name' | 'talking'
>

const MemberOverlay = ({
  audioMuted,
  handRaised,
  memberId,
  name,
  talking,
}: MemberOverlayProps) => {
  const overlayMap = useRoomStore(state => state.overlayMap)
  const roomSession = useRoomSessionStore(state => state.roomSession)
  const [overlayElement, setOverlayElement] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (!roomSession || !memberId) return

    const element = roomSession.getMemberOverlay(memberId)?.domElement
    if (element) {
      setOverlayElement(element)
    }

    return () => {
      setOverlayElement(null)
    }

    /**
     * There is a possibility that the members list is updated before the DOM element is injected.
     * To address this, we rely on the overlayMap.size to trigger re-renders.
     * The SDK updates this Map when adding or removing a DOM element.
     */
  }, [roomSession, memberId, overlayMap?.size])

  // If the overlay element is not available, render nothing
  if (!overlayElement) {
    return null
  }

  // Render overlay UI into the overlay element with React Portals
  // @https://react.dev/reference/react-dom/createPortal#rendering-react-components-into-non-react-dom-nodes
  return createPortal(
    <MemberOverlayUI
      audioMuted={audioMuted}
      handRaised={handRaised}
      memberId={memberId}
      name={name}
      talking={talking}
    />,
    overlayElement,
    memberId,
  )
}

type MemberOverlayUIProps = MemberOverlayProps

const MemberOverlayUI = ({
  audioMuted,
  handRaised,
  memberId,
  name,
  talking,
}: MemberOverlayUIProps) => {
  const [isPointerOverOverlay, setIsPointerOverOverlay] = useState(false)

  const nameRef = useRef<HTMLDivElement | null>(null)
  const parentRef = useRef<HTMLDivElement | null>(null)

  const selectedMemberId = useUiStore(state => state.selectedMemberId)
  const roomPanelOpen = useUiStore(state => state.roomPanelOpen)
  const roomPanelType = useUiStore(state => state.roomPanelType)

  const { closeRoomPanel, openRoomPanel, setAdvancedPanel } =
    useUiStoreActions()

  const isSameMemberId = selectedMemberId === memberId
  const isSameRoomPanelType = roomPanelType === 'advanced'

  const toggleAdvancedPanel = () => {
    // close the advanced panel if it's already open and the same member is selected
    if (roomPanelOpen && isSameRoomPanelType && isSameMemberId) {
      closeRoomPanel()
    } else {
      // open the advanced panel
      openRoomPanel()
      setAdvancedPanel(memberId)
    }
  }

  const isAdvancedPanelActive =
    isSameMemberId && roomPanelOpen && isSameRoomPanelType

  return (
    <article
      className="relative h-full w-full contain-layout @container"
      onPointerEnter={() => setIsPointerOverOverlay(true)}
      onPointerLeave={() => setIsPointerOverOverlay(false)}
      ref={parentRef}
    >
      <h1 className="sr-only">Overlay for {name}</h1>
      <div
        className={cn(
          'relative z-10 flex h-full w-full items-center justify-around gap-x-2 border-2 border-transparent transition-all duration-300 ease-in-out @3xs:items-start @3xs:justify-between @3xs:border-4',
          talking && 'border-negative-light',
        )}
      >
        <Icon
          tag="mic-off"
          size="xl"
          className={cn(
            'invisible h-auto min-w-6 max-w-10 @4xs:w-8 @sm:w-10 @md:mr-auto',
            audioMuted && 'visible',
          )}
          aria-hidden={!audioMuted}
          aria-label={audioMuted ? 'Microphone off' : undefined}
        />
        <Icon
          tag="raised-hand"
          size="xl"
          className={cn(
            'invisible h-auto min-w-6 max-w-10 @4xs:w-8 @sm:w-10 @md:ml-auto',
            handRaised && 'visible',
          )}
          aria-hidden={!handRaised}
          aria-label={handRaised ? 'Hand raised' : undefined}
        />
        <Feature name={FEATURES.MEMBER_OVERLAY_ADVANCED}>
          <Button
            className={cn(
              'group invisible m-0 flex min-w-6 max-w-10 flex-col rounded-none bg-transparent',
              'p-0 text-xs font-semibold text-neutral-light hover:bg-transparent',
              '@4xs:h-8 @4xs:w-8 @sm:h-10 @sm:w-10',
              (isPointerOverOverlay || isAdvancedPanelActive) && 'visible',
            )}
            onClick={toggleAdvancedPanel}
          >
            <div
              className={cn(
                'rounded-full p-1 transition-colors',
                isAdvancedPanelActive
                  ? 'gradient p-1'
                  : 'border-2 border-transparent p-0.5 delay-button group-hover:border-negative-light group-hover:bg-background',
              )}
            >
              <Icon
                // NOTE: the icon needs to scale with the button container including border + padding
                className={cn(
                  'h-5 w-5 @4xs:h-6 @4xs:w-6 @sm:h-7 @sm:w-7',
                  !isAdvancedPanelActive &&
                    'delay-button group-hover:fill-negative-light',
                )}
                tag="settings"
              />
            </div>
            <span className="sr-only">Advanced Settings for {name}</span>
          </Button>
        </Feature>
      </div>
      <footer
        className={cn(
          'absolute inset-x-0 bottom-0 flex items-center bg-gradient-to-t from-black to-transparent py-[0.125rem] @3xs:py-1',
        )}
      >
        <div
          className={cn(
            'relative z-20 truncate px-1 text-left text-xs text-background @3xs:px-2 @3xs:text-sm @md:text-base',
          )}
          ref={nameRef}
        >
          {/* show tooltip when pointer-over if name is truncated */}
          {isElementTruncated(nameRef.current) ? (
            <UncontrolledTooltip trigger={<p>{name}</p>}>
              <p>{name}</p>
            </UncontrolledTooltip>
          ) : (
            <p>{name}</p>
          )}
        </div>
      </footer>
    </article>
  )
}
